"use client";

import { useSearchParams } from "next/navigation";
import TextField from "../ui/textField";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { buildHrefWithRawQuery, cn } from "@/lib";
import CustomButton from "../ui/customButton";
import { TLoginMain, TLoginPayload } from "@/interfaces";
import { loginMainSchema } from "@/validations";
import { useLoginMutation } from "@/hooks/useAuth";
import ErrorMessage from "../ui/errorMessage";
import { useTranslations } from "next-intl";
import { useModalStore } from "@/store";
import Link from "next/link";
import { useToastStore } from "@/store/toastStore";
import { routes, staticQueryParams } from "@/routes";

const LoginMainForm = ({
  isPage = false,
  nextFormStep,
}: {
  isPage?: boolean;
  nextFormStep?: () => void;
}) => {
  const { closeModal } = useModalStore();
  const { setOpenToast } = useToastStore();
  const searchParams = useSearchParams();
  const t = useTranslations("auth");
  const {
    isPending: isLogging,
    mutate: loginFn,
    error: loginError,
  } = useLoginMutation();
  const {
    register,
    handleSubmit,
    formState: { errors: formError },
    watch,
  } = useForm<TLoginMain>({
    resolver: zodResolver(loginMainSchema),
  });
  const openToast = () => {
    setOpenToast({
      variant: "success",
      message: "ورود شما با موفقیت انجام شد",
    });
  };
  const onSubmit = (loginData: TLoginMain) => {
    const payload: TLoginPayload = {
      mobile: loginData.phoneNumber,
      password: loginData.password,
    };
    loginFn(payload, {
      onSuccess: () => {
        if (isPage) {
          window.location.href =
            searchParams?.get(staticQueryParams.redirect) ||
            routes.main.HOME.primaryPath;
        } else {
          closeModal();
          window.location.reload();
        }
        openToast();
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn("mb-4 w-full")}>
      <fieldset>
        <TextField
          {...register("phoneNumber")}
          placeholder={t("placeholders.phoneNumber")}
          numeric
          hasShadow
          aria-label="phone number"
          inputMode="numeric"
          className="mb-4"
          inputClassName="h-10"
          autoFocus
          maxLength={11}
          value={watch("phoneNumber")}
          error={formError.phoneNumber?.message}
        />
      </fieldset>

      <fieldset>
        <TextField
          {...register("password")}
          aria-label="password"
          hasShadow
          placeholder={t("placeholders.password")}
          type="password"
          value={watch("password")}
          error={formError.password?.message}
          className="mb-2"
          inputClassName="h-10"
        />
        <p className={cn("text-primary-main text-end text-sm")}>
          {!isPage ? (
            <span
              className="cursor-pointer text-sm md:text-base"
              title={t("forgotPassword")}
              onClick={nextFormStep}
            >
              {t("forgotPassword")}
            </span>
          ) : (
            <Link
              id="login-main-form-forgot-password"
              href={buildHrefWithRawQuery(
                routes.auth.FORGOT_PASSWORD.primaryPath,
                searchParams,
              )}
              className="cursor-pointer text-sm md:text-base"
            >
              {t("forgotPassword")}
            </Link>
          )}
        </p>
      </fieldset>
      {loginError && <ErrorMessage message={loginError.message} />}
      <CustomButton
        id="login-main-form-submit"
        type="submit"
        className="mt-8 w-max"
        isLoading={isLogging}
      >
        {t("buttons.loginSubmit")}
      </CustomButton>
    </form>
  );
};

export default LoginMainForm;
